import {
  Body,
  Caption,
  Heading,
  Icon,
  MediaHeader,
  Pill,
  Row,
  Stack,
  TONE,
  type Tone,
} from '@leanin/ui';
import { Layers } from 'lucide-react';
import { useTranslations } from 'use-intl';

import { toneFromId } from '@/lib/group-tone';
import styles from './LearningTrackCard.module.css';
import { LearningTrackCoverMosaic } from './LearningTrackCoverMosaic';
import type { LearningTrackSummary } from '@/lib/queries/learning-tracks';

type Props = {
  readonly track: LearningTrackSummary;
  /**
   * `grid` (default) — the full library card: mosaic, title, 2-line
   * description, resource count.
   * `rail` — the shelf variant: single-cover overlay card with a
   * LEARNING TRACK chip and an "N resources" chip.
   */
  readonly variant?: 'grid' | 'rail';
};

// Track tone cycle — no yellow (it reads muddy under the scrim); a track
// keeps its tone across visits. Exported so the rail's peek layer can
// tint with the SAME tone the card's cover fallback uses.
export const TRACK_TONES: ReadonlyArray<Tone> = [
  'burgundy',
  'navy',
  'emerald',
  'orange',
];

// Coverless fallback: a rich ink→accent→tint sweep instead of the pale
// tonal gradient, which washed to beige under the scrim.
function trackFallbackBg(tone: Tone): string {
  const t = TONE[tone];
  return `linear-gradient(160deg, ${t.ink} 0%, ${t.accent} 65%, ${t.bg} 130%)`;
}

// The stack-of-layers glyph is the tracks-only "playlist" mark (badge +
// resources chip). Not in the curated `I` map (single-feature use), so a
// feature-local lucide import per the curated-map rule.
const layersIcon = <Layers size="100%" strokeWidth={1.75} />;

// Learning Track card. The grid variant leads with a 2×2 mosaic of the
// first 4 resource cover images — the playlist-tile pattern from
// Spotify / Apple Music translated to a collection of curated resources.
// The rail variant is a single-cover overlay card. Both fall back to a
// tone-tinted placeholder when no items have covers.
//
// Sits inside a `CardLink` (no inner border/radius here — the wrapper
// CardLink uses `clip` and owns the padding/border).
export function LearningTrackCard({ track, variant = 'grid' }: Props) {
  const t = useTranslations('LearningTracks');

  if (variant === 'rail') {
    const cover = track.previewCovers[0];
    const tone = toneFromId(track.id, TRACK_TONES);
    return (
      <MediaHeader
        {...(cover !== undefined ? { src: cover } : {})}
        tone={tone}
        {...(cover === undefined ? { bg: trackFallbackBg(tone) } : {})}
        height={190}
        scrim
        topLeft={
          <span className={`${styles.chip} ${styles.chipLight}`}>
            <Icon size="sm">{layersIcon}</Icon>
            <span>{t('card.badge')}</span>
          </span>
        }
        topRight={
          !track.published ? (
            <Pill tone="neutral" size="sm">
              {t('card.draft')}
            </Pill>
          ) : undefined
        }
        bottomLeft={
          <Stack gap="sm" align="flex-start">
            {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold overlay-title exception as ResourceShelfCard */}
            <h3 className={styles.overlayTitle}>{track.title}</h3>
            <span className={`${styles.chip} ${styles.chipDark}`}>
              {t('card.resourceCount', { count: track.resourceCount })}
            </span>
          </Stack>
        }
      />
    );
  }

  return (
    <Stack gap="none" style={{ flex: 1, minWidth: 0 }}>
      <LearningTrackCoverMosaic
        covers={track.previewCovers}
        title={track.title}
      />
      <Stack p="xl" gap="md" style={{ flex: 1, minWidth: 0 }}>
        {!track.published ? (
          <Row gap="sm" align="center">
            <Pill tone="neutral" size="sm">
              {t('card.draft')}
            </Pill>
          </Row>
        ) : null}
        <Heading level={3}>{track.title}</Heading>
        {track.description.length > 0 ? (
          <Body
            size="sm"
            color="mute"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {track.description}
          </Body>
        ) : null}
        <Caption>
          {t('card.resourceCount', { count: track.resourceCount })}
        </Caption>
      </Stack>
    </Stack>
  );
}

